// R1-VISUAL Batch 2 — Slice 6: Login Redesign.
// GUARDRAILS: all data-testids preserved; auth flow (login/forgot) is byte-for-byte
// identical to prior file — only the left visual panel and the right-column
// typographic treatment changed.
import { homeRouteFor } from "../lib/roleHome";
import MfaChallenge from "../components/mfa/MfaChallenge";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import PasswordInput from "../components/PasswordInput";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Timer, AlertTriangle, ShieldCheck } from "lucide-react";

const LOGIN_PANEL = "/brand/login_visual_panel.jpg";
const LOGO_REVERSED = "/brand/erf_logo_horizontal_reversed.jpg";

function fmtSeconds(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m.toString().padStart(2, "0")}:${r.toString().padStart(2, "0")}`;
}

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [errCode, setErrCode] = useState("");
  const [mfa, setMfa] = useState(null);
  const [retrySec, setRetrySec] = useState(0);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [visibilityResetKey, setVisibilityResetKey] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  if (user && user.must_rotate_password) return <Navigate to="/auth/rotate" replace />;
  if (user) return <Navigate to={homeRouteFor(user)} replace />;

  const startCountdown = (seconds) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setRetrySec(seconds);
    timerRef.current = setInterval(() => {
      setRetrySec((prev) => {
        if (prev <= 1) { clearInterval(timerRef.current); timerRef.current = null; return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setErrCode("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u?.mfa) { setMfa(u.mfa); return; }
      navigate(u.must_rotate_password ? "/auth/rotate" : homeRouteFor(u));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        const retry = Number(detail?.retry_after_seconds) || 60;
        setErrCode("rate_limited");
        setErr(detail?.message || "Too many attempts. Please wait before trying again.");
        startCountdown(retry);
      } else if (status === 401) {
        setErrCode(detail?.code || "invalid_credentials");
        setErr(detail?.message || "Invalid email or password");
      } else {
        setErr(formatApiError(detail) || e.message);
      }
    } finally {
      setBusy(false);
      setVisibilityResetKey((k) => k + 1);
    }
  };

  const forgot = async () => {
    if (!email) { toast.error("Enter your email above first."); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If an account exists for that email, we've sent a password reset link. It expires in 1 hour.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setForgotBusy(false); }
  };

  const locked = retrySec > 0;

  if (mfa) {
    return (
      <MfaChallenge pending={mfa} onCancel={() => setMfa(null)}
        onDone={(u) => navigate(u.must_rotate_password ? "/auth/rotate" : homeRouteFor(u))} />
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col md:flex-row"
      data-testid="login-page"
      style={{ background: "#F5F1E8", fontFamily: '"Inter", system-ui, sans-serif' }}
    >
      {/* LEFT — Brand visual panel (desktop only). */}
      <div
        className="hidden md:flex md:w-1/2 relative overflow-hidden"
        style={{ minHeight: "100vh", backgroundColor: "#0B2545" }}
      >
        <img
          src={LOGIN_PANEL}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%", objectFit: "cover",
            opacity: 0.85,
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(180deg, rgba(11,37,69,0.65) 0%, rgba(11,37,69,0.85) 60%, rgba(11,37,69,0.95) 100%)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0,
            background: "radial-gradient(600px 380px at 25% 30%, rgba(217,184,122,0.20), transparent 65%)",
          }}
        />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full" style={{ color: "#F5F1E8" }}>
          <Link to="/" data-testid="login-brand-link" aria-label="ERF Enterprises home">
            <img
              src={LOGO_REVERSED}
              alt="ERF ENTERPRISES LLC"
              style={{ height: 56, width: "auto", display: "block" }}
            />
          </Link>

          <div style={{ maxWidth: 460 }}>
            <div
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase",
                color: "#D9B87A", marginBottom: 20,
              }}
            >
              ERF ConstructionOS · Operator Portal
            </div>
            <h2
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: 40, lineHeight: 1.15, fontWeight: 700,
                color: "#F5F1E8", margin: 0, letterSpacing: "-0.01em",
              }}
            >
              Disciplined construction. Documented decisions.
            </h2>
            <p style={{ marginTop: 18, fontSize: 15, lineHeight: 1.65, color: "rgba(245,241,232,0.82)" }}>
              Sign in to the ERF operating platform. Every action inside is written to an immutable audit ledger — the record of how the work actually got done.
            </p>
          </div>

          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderTop: "1px solid rgba(217,184,122,0.35)",
              paddingTop: 16,
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase",
              color: "rgba(245,241,232,0.55)",
            }}
          >
            <span>PA · NJ · DE · MD · DC · VA · NC · SC · GA · FL</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <ShieldCheck size={12} color="#D9B87A" /> Audit-tracked access
            </span>
          </div>
        </div>
      </div>

      {/* RIGHT — Sign-in form. */}
      <div className="flex-1 flex items-center justify-center p-6" style={{ minHeight: "100vh" }}>
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="login-form"
          style={{ display: "flex", flexDirection: "column", gap: 22 }}
        >
          {/* Mobile brand */}
          <div className="md:hidden flex justify-center mb-2">
            <Link to="/" data-testid="login-brand-link-mobile" aria-label="ERF Enterprises home">
              <img
                src="/brand/erf_logo_horizontal.jpg"
                alt="ERF ENTERPRISES LLC"
                style={{ height: 44, width: "auto", display: "block" }}
              />
            </Link>
          </div>

          <div>
            <div
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
                color: "#B08D57", marginBottom: 8,
              }}
            >
              Owner Access
            </div>
            <h1
              style={{
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: 30, fontWeight: 700, color: "#0B2545", letterSpacing: "-0.01em",
                margin: 0, lineHeight: 1.15,
              }}
            >
              Sign in
            </h1>
            <p style={{ fontSize: 13.5, color: "#1F2937", opacity: 0.75, marginTop: 8 }}>
              Authorised personnel only. All access is audited.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Label
              htmlFor="email"
              style={{
                fontFamily: '"Inter", sans-serif', fontSize: 11, fontWeight: 600,
                letterSpacing: "0.18em", textTransform: "uppercase",
                color: "#0B2545", opacity: 0.75,
              }}
            >
              Work email
            </Label>
            <Input
              id="email" type="email" value={email} autoComplete="username"
              onChange={(e) => setEmail(e.target.value)} placeholder="you@erf-enterprises.com"
              required data-testid="login-email-input"
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                background: "#FFFFFF",
                border: "1px solid #E5E1DA",
                height: 44,
                color: "#0B2545",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Label
              htmlFor="password"
              style={{
                fontFamily: '"Inter", sans-serif', fontSize: 11, fontWeight: 600,
                letterSpacing: "0.18em", textTransform: "uppercase",
                color: "#0B2545", opacity: 0.75,
              }}
            >
              Password
            </Label>
            <PasswordInput
              id="password"
              value={password}
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              required
              testId="login-password-input"
              resetVisibilityKey={visibilityResetKey}
              className="font-mono"
              style={{
                background: "#FFFFFF",
                border: "1px solid #E5E1DA",
                height: 44,
                color: "#0B2545",
              }}
            />
          </div>

          {err && !locked && (
            <div
              data-testid="login-error" data-error-code={errCode}
              style={{
                display: "flex", alignItems: "flex-start", gap: 8,
                background: "#FDECEC", border: "1px solid #F5B7B1",
                borderLeft: "3px solid #A93226",
                color: "#7B241C",
                padding: "10px 12px", borderRadius: 3, fontSize: 13,
              }}
            >
              <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>{err}</div>
            </div>
          )}
          {locked && (
            <div
              data-testid="login-rate-limit"
              style={{
                background: "#FDF3E1", border: "1px solid #E5C57C",
                borderLeft: "3px solid #B08D57",
                color: "#7A5C1E",
                padding: "12px 14px", borderRadius: 3, fontSize: 13,
              }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <Timer size={16} style={{ marginTop: 2, flexShrink: 0, color: "#B08D57" }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>Too many attempts</div>
                  <div style={{ fontSize: 12, marginTop: 2 }}>{err}</div>
                  <div
                    data-testid="login-rate-countdown"
                    style={{ marginTop: 8, fontFamily: '"IBM Plex Mono", monospace', fontSize: 16 }}
                  >
                    Try again in {fmtSeconds(retrySec)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <Button
            type="submit" disabled={busy || locked}
            data-testid="login-submit-button"
            style={{
              width: "100%", height: 46,
              background: locked ? "#C7B58A" : "#B08D57",
              color: "#0B2545",
              fontFamily: '"Inter", sans-serif',
              fontSize: 12, fontWeight: 700,
              letterSpacing: "0.18em", textTransform: "uppercase",
              borderRadius: 3, border: "none",
              boxShadow: locked ? "none" : "0 8px 20px -12px rgba(176,141,87,0.6)",
              cursor: (busy || locked) ? "not-allowed" : "pointer",
            }}
          >
            {locked ? `Locked · ${fmtSeconds(retrySec)}` : busy ? "Signing in…" : "Sign in"}
          </Button>

          <div
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              borderTop: "1px solid #E5E1DA", paddingTop: 14, fontSize: 12,
            }}
          >
            <button
              type="button" onClick={forgot} disabled={forgotBusy}
              data-testid="forgot-password-btn"
              style={{
                background: "transparent", border: "none", cursor: "pointer",
                color: "#0B2545", fontFamily: '"Inter", sans-serif',
                fontSize: 12, opacity: forgotBusy ? 0.55 : 0.85, padding: 0,
              }}
            >
              {forgotBusy ? "Sending…" : "Forgot password?"}
            </button>
            <Link
              to="/phase/mfa"
              title="Phase 10 hardening"
              data-testid="mfa-badge"
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 10, letterSpacing: "0.22em", textTransform: "uppercase",
                color: "#B08D57", opacity: 0.75, textDecoration: "none",
              }}
            >
              MFA · Phase 10
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
